import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminListArticles, adminPublishArticle } from "../api/adminCommunity";

const CATEGORIES = ["EVENT", "DJ", "AFFILIATE"];
const STATUSES = ["ALL", "DRAFT", "PUBLISHED"];

const Badge = ({ children, tone = "default" }) => {
    const cls =
        tone === "success"
            ? "bg-emerald-500/20 text-emerald-200"
            : tone === "warning"
                ? "bg-amber-500/20 text-amber-200"
                : "bg-white/10 text-slate-200";
    return <span className={`badge ${cls}`}>{children}</span>;
};

export default function AdminArticles() {
    const [q, setQ] = useState("");
    const [page, setPage] = useState(0);
    const [data, setData] = useState({ items: [], total: 0, size: 20 });
    const [category, setCategory] = useState("ALL");
    const [status, setStatus] = useState("ALL");
    const pageSize = 20;

    useEffect(() => {
        adminListArticles({ q, page, size: pageSize })
            .then(setData)
            .catch(console.error);
    }, [q, page]);

    // Client-side filters (non-breaking). If your API supports filters,
    // you can pass category/status directly when fetching instead.
    const filtered = useMemo(() => {
        let items = data.items || [];
        if (category !== "ALL") items = items.filter(a => a.category === category);
        if (status !== "ALL") {
            const wantPublished = status === "PUBLISHED";
            items = items.filter(a => Boolean(a.publishedAt) === wantPublished);
        }
        if (q.trim()) {
            const term = q.toLowerCase();
            items = items.filter(a =>
                [a.title, a.excerpt, a.category]
                    .filter(Boolean)
                    .some(s => String(s).toLowerCase().includes(term))
            );
        }
        return items;
    }, [data.items, category, status, q]);

    const publish = async (id) => {
        await adminPublishArticle(id);
        const fresh = await adminListArticles({ q, page, size: pageSize });
        setData(fresh);
    };

    return (
        <div className="container py-6 space-y-4">
            {/* Header */}
            <header className="card">
                <div className="md:flex md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="font-display text-3xl md:text-4xl">Community Articles</h1>
                        <p className="mt-1 text-slate-300">
                            Create, edit and publish articles shown in the Community section.
                        </p>
                    </div>
                    <Link to="/admin/articles/new" className="btn-primary">+ New Article</Link>
                </div>

                {/* Filter Row */}
                <div className="mt-4 grid sm:grid-cols-3 gap-2">
                    <label className="flex items-center gap-2 sm:col-span-2">
                        <span className="text-[11px] uppercase tracking-wider text-slate-400">Search</span>
                        <input
                            className="input w-full"
                            placeholder="Title, excerpt, category…"
                            value={q}
                            onChange={(e) => { setPage(0); setQ(e.target.value); }}
                        />
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        <label className="flex items-center gap-2">
                            <span className="text-[11px] uppercase tracking-wider text-slate-400">Category</span>
                            <select
                                className="input"
                                value={category}
                                onChange={(e) => { setCategory(e.target.value); setPage(0); }}
                            >
                                <option value="ALL">All</option>
                                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>
                        <label className="flex items-center gap-2">
                            <span className="text-[11px] uppercase tracking-wider text-slate-400">Status</span>
                            <select
                                className="input"
                                value={status}
                                onChange={(e) => { setStatus(e.target.value); setPage(0); }}
                            >
                                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </label>
                    </div>
                </div>
            </header>

            {/* Table */}
            <section className="card overflow-x-auto">
                {filtered.length === 0 ? (
                    <div className="text-slate-300 text-center py-10">
                        No articles found. Try adjusting your filters or{" "}
                        <Link to="/admin/articles/new" className="text-white underline">create a new article</Link>.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-slate-400">
                                <th className="py-2 px-3">Title</th>
                                <th className="py-2 px-3">Category</th>
                                <th className="py-2 px-3">Status</th>
                                <th className="py-2 px-3">Updated</th>
                                <th className="py-2 px-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(a => {
                                const isPublished = Boolean(a.publishedAt);
                                return (
                                    <tr key={a.id} className="border-t border-white/10">
                                        <td className="py-3 px-3">
                                            <div className="font-medium text-white truncate">{a.title}</div>
                                            {a.excerpt && (
                                                <div className="text-slate-400 truncate">{a.excerpt}</div>
                                            )}
                                        </td>
                                        <td className="py-3 px-3">
                                            <Badge>{a.category}</Badge>
                                        </td>
                                        <td className="py-3 px-3">
                                            {isPublished ? (
                                                <Badge tone="success">Published</Badge>
                                            ) : (
                                                <Badge tone="warning">Draft</Badge>
                                            )}
                                        </td>
                                        <td className="py-3 px-3">
                                            {isPublished
                                                ? new Date(a.publishedAt).toLocaleString()
                                                : (a.updatedAt ? new Date(a.updatedAt).toLocaleString() : "—")}
                                        </td>
                                        <td className="py-3 px-3">
                                            <div className="flex items-center justify-end gap-2">
                                                {!isPublished && (
                                                    <button onClick={() => publish(a.id)} className="btn-secondary">
                                                        Publish
                                                    </button>
                                                )}
                                                <Link to={`/admin/articles/${a.id}`} className="btn-primary">
                                                    Edit
                                                </Link>
                                                <Link
                                                    to={`/community/${a.slug}`}
                                                    className="btn-ghost"
                                                    target="_blank"
                                                    rel="noopener"
                                                >
                                                    View
                                                </Link>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </section>

            {/* Pagination */}
            <section className="flex items-center justify-center gap-2">
                <button
                    disabled={page === 0}
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    className="btn-secondary"
                >
                    Prev
                </button>
                <span className="text-sm text-slate-300">
                    Page <span className="text-white font-medium">{page + 1}</span>
                </span>
                <button
                    disabled={(page + 1) * (data.size ?? pageSize) >= (data.total ?? 0)}
                    onClick={() => setPage(p => p + 1)}
                    className="btn-secondary"
                >
                    Next
                </button>
            </section>
        </div>
    );
}
